import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';
import { ConfigProvider } from 'antd';
import { useSelector } from 'react-redux';
import { getDmsThemeConfig, normalizeAppearanceTheme } from '@/theme/theme-config';
import { cacheAppearanceTheme, readCachedAppearanceTheme } from '@/theme/theme-preference';

const AppearanceThemeContext = createContext(null);

// Consumers (e.g. the profile appearance selector) preview a theme instantly and
// only persist it on Save; closing/leaving unsaved reverts to the saved value.
export const useAppearanceTheme = () => {
  const context = useContext(AppearanceThemeContext);
  if (!context) {
    throw new Error('useAppearanceTheme must be used within a DmsThemeProvider');
  }
  return context;
};

const DmsThemeProvider = ({ children }) => {
  const persistedAppearanceTheme = useSelector(
    (state) => state.userDetails?.setting?.appearanceTheme
  );
  const [previewedAppearanceTheme, setPreviewedAppearanceTheme] = useState(null);

  const savedAppearanceTheme = normalizeAppearanceTheme(
    persistedAppearanceTheme ?? readCachedAppearanceTheme()
  );
  const appearanceTheme = normalizeAppearanceTheme(previewedAppearanceTheme ?? savedAppearanceTheme);

  useLayoutEffect(() => {
    document.documentElement.dataset.theme = appearanceTheme;
    document.documentElement.style.colorScheme = appearanceTheme;

    if (persistedAppearanceTheme != null && previewedAppearanceTheme == null) {
      cacheAppearanceTheme(appearanceTheme);
    }
  }, [appearanceTheme, persistedAppearanceTheme, previewedAppearanceTheme]);

  useLayoutEffect(() => {
    // Static Modal methods render outside the app's ConfigProvider tree.
    ConfigProvider.config({
      holderRender: (node) => (
        <ConfigProvider theme={getDmsThemeConfig(appearanceTheme)}>{node}</ConfigProvider>
      ),
    });

    return () => ConfigProvider.config({ holderRender: undefined });
  }, [appearanceTheme]);

  const previewAppearanceTheme = useCallback((value) => {
    setPreviewedAppearanceTheme(value == null ? null : normalizeAppearanceTheme(value));
  }, []);

  const revertAppearanceThemePreview = useCallback(() => {
    setPreviewedAppearanceTheme(null);
  }, []);

  const contextValue = useMemo(
    () => ({
      appearanceTheme,
      savedAppearanceTheme,
      isPreviewing: previewedAppearanceTheme != null,
      previewAppearanceTheme,
      revertAppearanceThemePreview,
    }),
    [appearanceTheme, savedAppearanceTheme, previewedAppearanceTheme, previewAppearanceTheme, revertAppearanceThemePreview]
  );

  return (
    <AppearanceThemeContext.Provider value={contextValue}>
      <ConfigProvider theme={getDmsThemeConfig(appearanceTheme)}>
        {children}
      </ConfigProvider>
    </AppearanceThemeContext.Provider>
  );
};

export default DmsThemeProvider;
